import { Component, computed, effect, input, signal } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { TNil } from '@app/app.interface';
import { ICtaForm } from '@campaign-form/campaign-form.interface';
import { EPlatformFeature } from '@enums/campaign.enum';
import { TExpectedKpi } from '@interfaces/campaign-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { ICreator } from '@pages/creators/creators.interface';
import { IGamePlayDeliverableForm } from '../../deliverable-step/deliverable-list/game-play/game-play.interface';
import { EExecutionType, IExecutionForm } from '../../execution-step/execution-list/execution/execution.interface';
import { ExecutionExpectedKpisComponent } from './execution-expected-kpis/execution-expected-kpis.component';

@Component({
  imports: [ExecutionExpectedKpisComponent, TranslateModule],
  selector: 'app-execution-expected-kpi-list',
  templateUrl: './execution-expected-kpi-list.component.html',
})
export class ExecutionExpectedKpiListComponent {
  public readonly formArray = input.required<FormArray<FormGroup<IExecutionForm>>>();
  public readonly platformFeature = input.required<EPlatformFeature>();
  public readonly availableKpis = input.required<TExpectedKpi[] | null>();
  public readonly creatorId = input.required<string | TNil>();
  public readonly isCustomMode = input.required<boolean>();
  public readonly creators = input.required<ICreator[]>();
  public readonly ctaForm = input.required<FormGroup<ICtaForm>>();

  public readonly isAllExpanded = input<boolean>(false);
  public readonly isViewMode = input<boolean>(false);
  public readonly gamePlayValues = input<FormArray<FormGroup<IGamePlayDeliverableForm>>['value']>([]);

  // Wave 10 follow-up - ctaForm is already threaded down here, so its own id doubles as
  // ctaTemplateId for kpi events without needing a separate input from expected-kpi-step.
  public readonly ctaTemplateId = computed(() => this.ctaForm().get('id')?.value ?? undefined);

  public readonly visibleExecutionControls = computed(() => {
    return this.getExecutionControlsForCurrentMode().filter(
      (control) => control.getRawValue().executionType !== EExecutionType.noExpectations,
    );
  });

  // no_expectations rules carry no KPI targets, so they are left out above - this lets the step
  // explain an otherwise empty list.
  public readonly hasOnlyNoExpectationsExecutions = computed(() => {
    const executionControls = this.getExecutionControlsForCurrentMode();

    return executionControls.length > 0 && !this.visibleExecutionControls().length;
  });

  private readonly formArrayValueVersion = signal<number>(0);

  private getExecutionControlsForCurrentMode(): FormGroup<IExecutionForm>[] {
    this.formArrayValueVersion();

    return this.formArray().controls.filter((control) => {
      return (
        !control.value.exclusiveTo ||
        this.isViewMode() ||
        (this.isCustomMode() && control.value.exclusiveTo === this.creatorId())
      );
    });
  }

  constructor() {
    effect((onCleanup) => {
      const formArray = this.formArray();
      const formArraySubscription = formArray.valueChanges.subscribe(() => {
        this.formArrayValueVersion.update((version) => version + 1);
      });

      onCleanup(() => {
        formArraySubscription.unsubscribe();
      });
    });
  }
}
